/**
 * GS-Utils 国际化管理模块
 * 复用 LocaleManager 的语言设置，独立维护翻译内容
 * write by cursor
 */

import { getLocale, StandardLocale } from 'src/gs-utils/core/storage/LocaleManager';

// 导入各语言翻译文件
import zhCN from './locales/zh-CN.json';
import zhTW from './locales/zh-TW.json';
import enUS from './locales/en-US.json';
import koKR from './locales/ko-KR.json';
import jaJP from './locales/ja-JP.json';
import plPL from './locales/pl-PL.json';

/**
 * 翻译数据类型定义
 * write by cursor
 */
export interface TranslationData {
  [key: string]: string;
}

/**
 * 所有语言的翻译数据映射
 * write by cursor
 */
const GS_TRANSLATIONS: Record<StandardLocale, TranslationData> = {
  [StandardLocale.ZH_CN]: zhCN,
  [StandardLocale.ZH_TW]: zhTW,
  [StandardLocale.EN_US]: enUS,
  [StandardLocale.KO_KR]: koKR,
  [StandardLocale.JA_JP]: jaJP,
  [StandardLocale.PL_PL]: plPL,
};

/**
 * GS-Utils 国际化管理类
 * write by cursor
 */
class GSI18n {
  private static instance: GSI18n;

  /**
   * 私有构造函数，实现单例模式
   */
  private constructor() {
    // 初始化时不做任何操作
  }

  /**
   * 获取单例实例
   * @returns GSI18n 实例
   * write by cursor
   */
  public static getInstance(): GSI18n {
    if (!GSI18n.instance) {
      GSI18n.instance = new GSI18n();
    }
    return GSI18n.instance;
  }

  /**
   * 获取当前语言的翻译数据
   * @returns 当前语言的翻译数据对象
   * write by cursor
   */
  private getCurrentTranslations(): TranslationData {
    const currentLocale = getLocale() as StandardLocale;
    return GS_TRANSLATIONS[currentLocale] || GS_TRANSLATIONS[StandardLocale.EN_US];
  }

  /**
   * 翻译函数 - 根据key获取对应的翻译文本
   * @param key 翻译键值
   * @param params 可选的参数对象，支持模板替换
   * @returns 翻译后的文本，如果未找到则返回原key
   * write by cursor
   */
  public translate(key: string, params?: Record<string, string | number>): string {
    try {
      const translations = this.getCurrentTranslations();
      let value = translations[key];

      // 如果找不到翻译，返回原key并输出警告
      if (!value) {
        console.warn(`[GS-Utils I18n] Translation key not found: ${key}`);
        return key;
      }

      // 如果有参数，进行模板替换
      if (params && typeof params === 'object') {
        for (const [paramKey, paramValue] of Object.entries(params)) {
          if (typeof paramValue === 'string' || typeof paramValue === 'number') {
            // 支持 {{paramKey}} 格式的模板替换
            value = value.replace(new RegExp(`\\{\\{${paramKey}\\}\\}`, 'g'), String(paramValue));
          }
        }
      }

      return value;
    } catch (error) {
      console.error('[GS-Utils I18n] Translation error:', error);
      return key;
    }
  }

  /**
   * 检查是否存在指定的翻译键
   * @param key 翻译键值
   * @returns 是否存在该翻译键
   * write by cursor
   */
  public hasTranslation(key: string): boolean {
    const translations = this.getCurrentTranslations();
    return key in translations;
  }

  /**
   * 获取当前语言的所有翻译键
   * @returns 翻译键数组
   * write by cursor
   */
  public getAvailableKeys(): string[] {
    const translations = this.getCurrentTranslations();
    return Object.keys(translations);
  }

  /**
   * 获取当前使用的语言代码
   * @returns 当前语言代码
   * write by cursor
   */
  public getCurrentLocale(): string {
    return getLocale();
  }
}

// 创建单例实例
const gsI18nInstance = GSI18n.getInstance();

/**
 * GS-Utils 翻译函数 - 简化版API
 * @param key 翻译键值
 * @param params 可选的参数对象，支持模板替换
 * @returns 翻译后的文本
 * write by cursor
 */
export const gsT = (key: string, params?: Record<string, string | number>): string => {
  return gsI18nInstance.translate(key, params);
};

/**
 * 检查翻译键是否存在
 * @param key 翻译键值
 * @returns 是否存在该翻译键
 * write by cursor
 */
export const gsHasT = (key: string): boolean => {
  return gsI18nInstance.hasTranslation(key);
};

/**
 * 获取当前语言的所有可用翻译键
 * @returns 翻译键数组
 * write by cursor
 */
export const gsGetKeys = (): string[] => {
  return gsI18nInstance.getAvailableKeys();
};

/**
 * 获取当前语言代码
 * @returns 当前语言代码
 * write by cursor
 */
export const gsGetLocale = (): string => {
  return gsI18nInstance.getCurrentLocale();
};

// 导出主要的翻译函数和工具函数
export { GSI18n };

// 默认导出翻译函数
export default gsT;
